/**
 * VAIR Scheduler
 *
 * Converts VAIR algorithm output to the standard Round format used by the system.
 * This allows VAIR events to work with the existing GameDay UI and match structure.
 */
import { Round } from 'constants/tournaments';
import { DEFAULT_TIME_PER_MATCH } from 'constants/vair';
import { generateVairSchedule4Players } from './algorithm-4p';
import { generateVairSchedule5Players } from './algorithm-5p';
import { generateVairSchedule6Players } from './algorithm-6p';
import { generateVairSchedule7Players } from './algorithm-7p';
import { generateVairSchedule8Players } from './algorithm-8p';
import { generateVairSchedule9Players } from './algorithm-9p';
import { VairSchedule } from './types';

const VAIR_PLAYER_COUNTS = [4, 5, 6, 7, 8, 9] as const;
type VairPlayerCount = (typeof VAIR_PLAYER_COUNTS)[number];

/**
 * VAIR config per player count (from algorithm files).
 * - games: total games/rounds
 * - courts: 1 for 4-7p, 2 for 8-9p
 * - matchesPerGame: 1 for 4-7p, 2 for 8-9p
 * - teamLimit: total matches (games × matchesPerGame)
 * - teamsPerGame: doubles pairs playing per game (2 or 4)
 * - rounds: number of round blocks (2 for 4-5p, 1 for others)
 * - byesPerGame: players with bye each game
 * - gamesPerPlayer: games each player plays
 */
export const VAIR_PLAYER_COUNT_CONFIG: Record<
  VairPlayerCount,
  {
    games: number;
    courts: number;
    matchesPerGame: number;
    teamLimit: number;
    teamsPerGame: number;
    rounds: number;
    byesPerGame: number;
    gamesPerPlayer: number;
  }
> = {
  4: {
    games: 8,
    courts: 1,
    matchesPerGame: 1,
    teamLimit: 8,
    teamsPerGame: 2,
    rounds: 2,
    byesPerGame: 0,
    gamesPerPlayer: 8,
  },
  5: {
    games: 10,
    courts: 1,
    matchesPerGame: 1,
    teamLimit: 10,
    teamsPerGame: 2,
    rounds: 2,
    byesPerGame: 1,
    gamesPerPlayer: 8,
  },
  6: {
    games: 9,
    courts: 1,
    matchesPerGame: 1,
    teamLimit: 9,
    teamsPerGame: 2,
    rounds: 1,
    byesPerGame: 2,
    gamesPerPlayer: 6,
  },
  7: {
    games: 7,
    courts: 1,
    matchesPerGame: 1,
    teamLimit: 7,
    teamsPerGame: 2,
    rounds: 1,
    byesPerGame: 3,
    gamesPerPlayer: 4,
  },
  8: {
    games: 7,
    courts: 2,
    matchesPerGame: 2,
    teamLimit: 14,
    teamsPerGame: 4,
    rounds: 1,
    byesPerGame: 0,
    gamesPerPlayer: 7,
  },
  9: {
    games: 9,
    courts: 2,
    matchesPerGame: 2,
    teamLimit: 18,
    teamsPerGame: 4,
    rounds: 1,
    byesPerGame: 1,
    gamesPerPlayer: 8,
  },
};

export function getVairConfigForPlayerCount(
  numPlayers: number,
): (typeof VAIR_PLAYER_COUNT_CONFIG)[VairPlayerCount] {
  const config = VAIR_PLAYER_COUNT_CONFIG[numPlayers as VairPlayerCount];
  if (!config) throw new Error(UNSUPPORTED_PLAYERS_MSG(numPlayers));
  return config;
}

/**
 * Minimum games across all VAIR player counts (4–9).
 * Use when player count is unknown (e.g. Quick Create before registrations).
 */
export function getMinimumVairGames(): number {
  return Math.min(...Object.values(VAIR_PLAYER_COUNT_CONFIG).map((c) => c.games));
}

/**
 * Maximum courts required for any VAIR player count (2 for 8–9 players).
 * Use when player count is unknown (e.g. Quick Create before registrations).
 */
export function getMaximumVairCourts(): number {
  return Math.max(...Object.values(VAIR_PLAYER_COUNT_CONFIG).map((c) => c.courts));
}

/**
 * Maximum teams per game across all VAIR player counts (4 for 8–9 players).
 * Use when player count is unknown (e.g. Quick Create before registrations).
 */
export function getMaximumVairTeamsPerGame(): number {
  return Math.max(...Object.values(VAIR_PLAYER_COUNT_CONFIG).map((c) => c.teamsPerGame));
}

const SCHEDULE_GENERATORS: Record<VairPlayerCount, (timePerMatch: number) => VairSchedule> = {
  4: generateVairSchedule4Players,
  5: generateVairSchedule5Players,
  6: generateVairSchedule6Players,
  7: generateVairSchedule7Players,
  8: generateVairSchedule8Players,
  9: generateVairSchedule9Players,
};

const UNSUPPORTED_PLAYERS_MSG = (n: number) =>
  `VAIR not implemented for ${n} players. Supported: ${VAIR_PLAYER_COUNTS.join(', ')}`;

function teamKey(p1: number, p2: number): string {
  return [p1, p2].sort((a, b) => a - b).join(',');
}

/** Builds team index map and team→players mapping from a VAIR schedule. */
function buildTeamMaps(vairSchedule: VairSchedule): {
  indexByKey: Map<string, number>;
  playersByIndex: Map<number, number[]>;
} {
  const indexByKey = new Map<string, number>();
  const playersByIndex = new Map<number, number[]>();
  let nextIndex = 0;

  for (const round of vairSchedule.rounds) {
    for (const match of round.matches) {
      for (const team of [match.team1, match.team2]) {
        const players = [team.player1, team.player2].sort((a, b) => a - b);
        const key = teamKey(team.player1, team.player2);
        if (!indexByKey.has(key)) {
          indexByKey.set(key, nextIndex);
          playersByIndex.set(nextIndex, players);
          nextIndex++;
        }
      }
    }
  }

  return { indexByKey, playersByIndex };
}

function convertVairScheduleToRounds(vairSchedule: VairSchedule): Round[] {
  const { indexByKey } = buildTeamMaps(vairSchedule);

  return vairSchedule.rounds.map((vairRound) => ({
    matches: vairRound.matches.map((m) => ({
      teamA: indexByKey.get(teamKey(m.team1.player1, m.team1.player2))!,
      teamB: indexByKey.get(teamKey(m.team2.player1, m.team2.player2))!,
      court: m.court,
      time: m.time,
    })),
    byes: vairRound.byes,
  }));
}

function extractTeamMappings(vairSchedule: VairSchedule): Map<number, number[]> {
  return buildTeamMaps(vairSchedule).playersByIndex;
}

function getGenerator(numPlayers: number): (timePerMatch: number) => VairSchedule {
  const generator = SCHEDULE_GENERATORS[numPlayers as VairPlayerCount];
  if (!generator) throw new Error(UNSUPPORTED_PLAYERS_MSG(numPlayers));
  return generator;
}

export function vairScheduler4Players(timePerMatch: number): Round[] {
  return convertVairScheduleToRounds(generateVairSchedule4Players(timePerMatch));
}
export function vairScheduler5Players(timePerMatch: number): Round[] {
  return convertVairScheduleToRounds(generateVairSchedule5Players(timePerMatch));
}
export function vairScheduler6Players(timePerMatch: number): Round[] {
  return convertVairScheduleToRounds(generateVairSchedule6Players(timePerMatch));
}
export function vairScheduler7Players(timePerMatch: number): Round[] {
  return convertVairScheduleToRounds(generateVairSchedule7Players(timePerMatch));
}
export function vairScheduler8Players(timePerMatch: number): Round[] {
  return convertVairScheduleToRounds(generateVairSchedule8Players(timePerMatch));
}
export function vairScheduler9Players(timePerMatch: number): Round[] {
  return convertVairScheduleToRounds(generateVairSchedule9Players(timePerMatch));
}

export function getVairTeamMappings4Players(
  timePerMatch = DEFAULT_TIME_PER_MATCH,
): Map<number, number[]> {
  return extractTeamMappings(generateVairSchedule4Players(timePerMatch));
}
export function getVairTeamMappings5Players(
  timePerMatch = DEFAULT_TIME_PER_MATCH,
): Map<number, number[]> {
  return extractTeamMappings(generateVairSchedule5Players(timePerMatch));
}
export function getVairTeamMappings6Players(
  timePerMatch = DEFAULT_TIME_PER_MATCH,
): Map<number, number[]> {
  return extractTeamMappings(generateVairSchedule6Players(timePerMatch));
}
export function getVairTeamMappings7Players(
  timePerMatch = DEFAULT_TIME_PER_MATCH,
): Map<number, number[]> {
  return extractTeamMappings(generateVairSchedule7Players(timePerMatch));
}
export function getVairTeamMappings8Players(
  timePerMatch = DEFAULT_TIME_PER_MATCH,
): Map<number, number[]> {
  return extractTeamMappings(generateVairSchedule8Players(timePerMatch));
}
export function getVairTeamMappings9Players(
  timePerMatch = DEFAULT_TIME_PER_MATCH,
): Map<number, number[]> {
  return extractTeamMappings(generateVairSchedule9Players(timePerMatch));
}

export function getVairSchedulerForPlayerCount(
  numPlayers: number,
  _numCourts: number,
  timePerMatch: number,
): Round[] {
  return convertVairScheduleToRounds(getGenerator(numPlayers)(timePerMatch));
}

export function getVairTeamMappingsForPlayerCount(
  numPlayers: number,
  timePerMatch = DEFAULT_TIME_PER_MATCH,
): Map<number, number[]> {
  return extractTeamMappings(getGenerator(numPlayers)(timePerMatch));
}

export function getMinimumCourtsForPlayerCount(numPlayers: number): number {
  return getVairConfigForPlayerCount(numPlayers).courts;
}

export default vairScheduler8Players;
